import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Generate or retrieve a stable per-device identifier (persists across logins)
const getDeviceId = () => {
  let id = localStorage.getItem("quang_device_id");
  if (!id) {
    id = (crypto.randomUUID && crypto.randomUUID()) || `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("quang_device_id", id);
  }
  return id;
};

const defaultDeviceLabel = () => {
  const ua = navigator.userAgent || "";
  if (/iPad/i.test(ua)) return "iPad";
  if (/iPhone/i.test(ua)) return "iPhone";
  if (/Android/i.test(ua) && /Mobile/i.test(ua)) return "Android Phone";
  if (/Android/i.test(ua)) return "Android Tablet";
  if (/Mac/i.test(ua)) return "Mac";
  if (/Windows/i.test(ua)) return "PC Windows";
  if (/Linux/i.test(ua)) return "Linux";
  return "Appareil";
};

const AdminLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [autoLoginTried, setAutoLoginTried] = useState(false);
  const navigate = useNavigate();
  const ranAutoLoginRef = useRef(false);

  // ---- AUTO-LOGIN ON TRUSTED DEVICE ----
  useEffect(() => {
    if (ranAutoLoginRef.current) return;
    ranAutoLoginRef.current = true;

    const existingToken = localStorage.getItem("quang_admin_token");
    if (existingToken) {
      navigate("/admin/dashboard");
      return;
    }
    const deviceId = localStorage.getItem("quang_device_id");
    const deviceToken = localStorage.getItem("quang_device_token");
    if (!deviceId || !deviceToken) {
      setAutoLoginTried(true);
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/admin/login/device", {
          device_id: deviceId,
          device_token: deviceToken,
        });
        localStorage.setItem("quang_admin_token", data.token);
        navigate("/admin/dashboard");
      } catch (err) {
        // Device no longer trusted (revoked or backend reset) — clear stale token
        localStorage.removeItem("quang_device_token");
        setAutoLoginTried(true);
      }
    })();
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const deviceId = getDeviceId();
      const { data } = await api.post("/admin/login", {
        username,
        password,
        device_id: deviceId,
        device_label: defaultDeviceLabel(),
      });
      localStorage.setItem("quang_admin_token", data.token);
      if (data.device_token) {
        localStorage.setItem("quang_device_token", data.device_token);
      }
      toast.success("Bienvenue · cet appareil est maintenant reconnu");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error("Identifiants invalides");
    } finally {
      setLoading(false);
    }
  };

  // Brief loading state while we try silent auto-login (avoid login form flash)
  if (!autoLoginTried) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-500" data-testid="admin-autologin-loader" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-5">
      <div className="absolute top-6 left-6 text-xs uppercase tracking-[0.2em] text-neutral-500">
        Quang · Espace gestion
      </div>
      <div className="w-full max-w-md bg-[#111] border border-neutral-800 rounded-xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-md bg-[#D90429] flex items-center justify-center">
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Connexion admin</h1>
            <p className="text-xs text-neutral-500">Accès réservé au restaurateur</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold text-neutral-300">Identifiant</Label>
            <Input
              data-testid="admin-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="mt-1.5 bg-[#0A0A0A] border-neutral-700 text-white focus-visible:ring-[#D90429]"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold text-neutral-300">Mot de passe</Label>
            <Input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-1.5 bg-[#0A0A0A] border-neutral-700 text-white focus-visible:ring-[#D90429]"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="w-full bg-[#D90429] hover:bg-[#BF0424] text-white rounded-md h-11 font-bold tracking-wide mt-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Se connecter"}
          </Button>
          <p className="text-[11px] text-neutral-500 text-center pt-2">
            Cet appareil sera mémorisé · vous n&apos;aurez plus besoin du mot de passe ici.
          </p>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
